/**
 * MSI / Plessey decoder — public API.
 *
 * Self-contained module. No external dependencies (no ZBar, no ZXing,
 * no QuaggaJS). Pure TypeScript + a TypedArray here and there. All
 * code in this directory is proprietary RDM code.
 *
 * Typical usage from the BarcodeScanner component:
 *
 *   import { decodeMSIFromImageData } from '@/utils/scanner/msi';
 *
 *   // In your detect loop, after drawing the camera frame to a canvas:
 *   const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
 *   const result = decodeMSIFromImageData(imageData);
 *   if (result) {
 *     onScan(result.digits);
 *   }
 *
 * Returns null when nothing decoded — call as often as you like;
 * extremely cheap when there's no barcode present.
 */

export { decodeScanline, decodeScanlineWithTraces } from './decoder';
export {
  extractScanlines,
  imageDataToGray,
  imageDataToScanlines,
  otsuThreshold,
  rowToRuns,
} from './imageProcessor';
export { encodeMSI, renderSymbolToImage } from './encoder';
export {
  appendChecksum,
  computeMod10,
  computeMod11,
  validateAndStrip,
} from './checksum';
export {
  BIT_PATTERNS,
  DEFAULT_CHECKSUM_ORDER,
  DIGIT_PATTERNS,
  NARROW,
  START_PATTERN,
  STOP_PATTERN,
  WIDE,
} from './lookup';
export type {
  ChecksumVariant,
  EncodeOptions,
  EncodedSymbol,
  GrayImage,
  MSIDecodeAttemptTrace,
  MSIDecodeOptions,
  MSIDecodeResult,
  MSIDecodeRunResult,
  MSIRejectReason,
  Scanline,
  ScanlineExtractionOptions,
} from './_exports';

import type { MSIDecodeAttemptTrace, MSIDecodeOptions, MSIDecodeResult } from './types';
import { decodeScanline, decodeScanlineWithTraces } from './decoder';
import {
  buildConsensusScanline,
  extractScanlines,
  imageDataToGray,
  type ScanlineExtractionOptions,
} from './imageProcessor';

// A denoised consensus scanline (vertical projection across the barcode band,
// #2209) is far more reliable than any single noisy row, so it counts as
// several corroborating votes rather than one.
const CONSENSUS_VOTE_WEIGHT = 3;

/**
 * Top-level convenience: take an ImageData (RGBA from a 2D canvas) and
 * return the best MSI decode found, or null.
 *
 * Tries every extracted scanline, then applies cross-scanline voting:
 * a candidate is only returned if the same digit string was decoded
 * from at least `voteThreshold` distinct scanlines. This compensates
 * for the relaxed quiet-zone / start-bar gates (see #1873) — single
 * spurious decodes get dropped; real barcodes show up on many adjacent
 * scanlines and easily clear the vote.
 */
export function decodeMSIFromImageData(
  imageData: ImageData,
  options: { decode?: MSIDecodeOptions; extract?: ScanlineExtractionOptions; voteThreshold?: number } = {},
): MSIDecodeResult | null {
  const gray = imageDataToGray(imageData);
  const scanlines = extractScanlines(gray, options.extract);
  const votes = new Map<string, { result: MSIDecodeResult; count: number }>();
  const addVote = (r: MSIDecodeResult | null, weight: number) => {
    if (!r) return;
    const key = `${r.checksum}:${r.digits}`;
    const slot = votes.get(key);
    if (slot) {
      slot.count += weight;
      if (r.confidence > slot.result.confidence) slot.result = r;
    } else {
      votes.set(key, { result: r, count: weight });
    }
  };
  for (const scan of scanlines) addVote(decodeScanline(scan, options.decode), 1);
  // #2209 — denoised consensus scanline as a strong corroborating vote.
  const consensus = buildConsensusScanline(gray, options.extract);
  if (consensus) addVote(decodeScanline(consensus, options.decode), CONSENSUS_VOTE_WEIGHT);
  const threshold = options.voteThreshold ?? 2;
  let best: MSIDecodeResult | null = null;
  for (const { result, count } of votes.values()) {
    if (count < threshold) continue;
    if (
      !best ||
      result.digits.length > best.digits.length ||
      (result.digits.length === best.digits.length && result.confidence > best.confidence)
    ) {
      best = result;
    }
  }
  return best;
}

/**
 * Trace variant of `decodeMSIFromImageData` — same scan but returns
 * every attempt with reason for rejection. Used exclusively by the
 * diagnostics page (#1873) to surface near-miss telemetry. Production
 * scanner path uses the plain `decodeMSIFromImageData` for speed (no
 * trace allocation per attempt).
 *
 * Applies the same cross-scanline voting as the plain variant so the
 * `best` field reflects production behavior. Also returns the top N
 * "promising" attempts (rejected past the ratio gate) with their raw
 * run widths attached, so the admin diagnostics view can replay the
 * decoder against actual bar data.
 */
export function decodeMSIFromImageDataWithTraces(
  imageData: ImageData,
  options: {
    decode?: MSIDecodeOptions;
    extract?: ScanlineExtractionOptions;
    voteThreshold?: number;
    /** Keep at most this many "promising" attempts with raw runs attached. Default 8. */
    keepPromising?: number;
  } = {},
): {
  best: MSIDecodeResult | null;
  attempts: MSIDecodeAttemptTrace[];
  scanlinesExtracted: number;
  promising: MSIDecodeAttemptTrace[];
  voteSummary: Array<{ digits: string; checksum: string; count: number; confidence: number }>;
  /**
   * Pixel-domain bounding box of the WINNING decode aggregated across the
   * scanlines that voted for it, plus partial-read flags (#2205). Drives the
   * diagnostics overlay. null when nothing won the vote.
   */
  bestBox: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    /** ~no quiet zone on a side → the tag ran off that edge (cut-off partial). */
    touchedLeftEdge: boolean;
    touchedRightEdge: boolean;
    leadingQuietModules: number;
    trailingQuietModules: number;
  } | null;
} {
  const decodeOpts: MSIDecodeOptions = { ...(options.decode ?? {}), dumpRunsForPromising: true };
  const gray = imageDataToGray(imageData);
  const scanlines = extractScanlines(gray, options.extract);
  const votes = new Map<string, { result: MSIDecodeResult; count: number; results: MSIDecodeResult[] }>();
  const allAttempts: MSIDecodeAttemptTrace[] = [];
  const addVote = (r: MSIDecodeResult | null, weight: number) => {
    if (!r) return;
    const key = `${r.checksum}:${r.digits}`;
    const slot = votes.get(key);
    if (slot) {
      slot.count += weight;
      slot.results.push(r);
      if (r.confidence > slot.result.confidence) slot.result = r;
    } else {
      votes.set(key, { result: r, count: weight, results: [r] });
    }
  };
  for (const scan of scanlines) {
    const { best: r, attempts } = decodeScanlineWithTraces(scan, decodeOpts);
    allAttempts.push(...attempts);
    addVote(r, 1);
  }
  // #2209 — denoised consensus scanline as a strong corroborating vote.
  const consensus = buildConsensusScanline(gray, options.extract);
  if (consensus) {
    const { best: cr, attempts } = decodeScanlineWithTraces(consensus, decodeOpts);
    allAttempts.push(...attempts);
    addVote(cr, CONSENSUS_VOTE_WEIGHT);
  }
  const threshold = options.voteThreshold ?? 2;
  let best: MSIDecodeResult | null = null;
  let bestKey: string | null = null;
  for (const [key, { result, count }] of votes.entries()) {
    if (count < threshold) continue;
    if (
      !best ||
      result.digits.length > best.digits.length ||
      (result.digits.length === best.digits.length && result.confidence > best.confidence)
    ) {
      best = result;
      bestKey = key;
    }
  }
  // Aggregate the winning decode's box across the scanlines that voted for it.
  const QUIET_EDGE_MODULES = 2; // < this much quiet on a side ⇒ "touched edge"
  let bestBox: ReturnType<typeof decodeMSIFromImageDataWithTraces>['bestBox'] = null;
  if (bestKey) {
    const winners = votes.get(bestKey)!.results.filter((r) => r.span);
    if (winners.length > 0) {
      const x0 = Math.min(...winners.map((r) => r.span!.x0));
      const x1 = Math.max(...winners.map((r) => r.span!.x1));
      const y0 = Math.min(...winners.map((r) => r.span!.y));
      const y1 = Math.max(...winners.map((r) => r.span!.y));
      const leadingQuietModules = Math.min(...winners.map((r) => r.leadingQuietModules ?? 0));
      const trailingQuietModules = Math.min(...winners.map((r) => r.trailingQuietModules ?? 0));
      bestBox = {
        x0,
        y0,
        x1,
        y1,
        leadingQuietModules,
        trailingQuietModules,
        touchedLeftEdge: leadingQuietModules < QUIET_EDGE_MODULES,
        touchedRightEdge: trailingQuietModules < QUIET_EDGE_MODULES,
      };
    }
  }
  const voteSummary = [...votes.entries()]
    .map(([, v]) => ({
      digits: v.result.digits,
      checksum: v.result.checksum,
      count: v.count,
      confidence: v.result.confidence,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Promising = anything that got past the ratio gate (has `runs`),
  // ranked by how far it made it through the pipeline.
  const rank: Record<string, number> = {
    accepted: 0,
    checksum_failed: 1,
    low_confidence: 2,
    too_short: 3,
    no_digit_match: 4,
    quiet_zone_too_short: 5,
    trailing_quiet_too_short: 5,
    start_bar_too_narrow: 6,
    ratio_out_of_band: 99,
  };
  const promising = allAttempts
    .filter((a) => a.runs && a.runs.length > 0)
    .sort((a, b) => (rank[a.reason] ?? 50) - (rank[b.reason] ?? 50))
    .slice(0, options.keepPromising ?? 8);

  return {
    best,
    attempts: allAttempts,
    scanlinesExtracted: scanlines.length,
    promising,
    voteSummary,
    bestBox,
  };
}
